import {
  isFestpreisOnlyAuftragsart,
  isFpComment,
  isNwPlusFpAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";
import { api } from "@/lib/apiClient";

/**
 * Auswertungen (Hero-Zeiten).
 *
 * Performance-Strategie: Es wird pro Zeitraum genau EIN ungefilterter Report
 * geladen (status=all, ohne Personen-Filter, inkl. Full-Lizenzen). Alle
 * Filter (Person, Status, Lizenz) laufen client-seitig und sind damit sofort.
 * Zusätzlich hält ein Modul-Cache die Antworten pro Zeitraum
 * (stale-while-revalidate), sodass ein erneuter Besuch sofort rendert.
 */

export type HeroHourMarkStatus =
  | "festpreis"
  | "auf_nachweis"
  | "ohne_berechnung"
  | "gewaehrleistung";

export interface HeroUsageRow {
  hero_uuid: string;
  partner_name: string | null;
  partner_account_type: string | null;
  comment: string | null;
  work_date: string | null;
  duration: number | null;
  duration_in_seconds: number | null;
  used_seconds: number | null;
  used_timesheet_id: number | null;
  timesheet_number: number | null;
  auftrag_id: number | null;
  auftragsart: string | null;
  projekt?: string | null;
  assignment_state?: string | null;
  hour_mark_status?: HeroHourMarkStatus | string | null;
  hour_mark_timesheet_id?: number | null;
  hour_mark_timesheet_number?: number | string | null;
  hour_mark_timesheet_status?: string | null;
  fp_marked_local?: boolean | null;
  partial_data?: boolean;
  tracking_category_internal_name?: string | null;
  tracking_category_description?: string | null;
  tracking_category_is_protected?: boolean | null;
  tracking_category_is_active?: boolean | null;
}

export type HeroUsageStatusFilter =
  | "all"
  | "assigned"
  | "unassigned"
  | "unmapped"
  | "festpreis"
  | "auf_nachweis"
  | "ohne_berechnung"
  | "gewaehrleistung";

export type HeroUsageAssignmentState = "assigned" | "unassigned" | "partial" | "unmapped";

export interface HeroUsageFilters {
  person: string;
  status: HeroUsageStatusFilter;
  from: string;
  to: string;
  hideFullLicense: boolean;
}

export interface HeroUsageTotals {
  assignedSeconds: number;
  unassignedSeconds: number;
  unassignedMappedSeconds: number;
  unmappedSeconds: number;
  partialSeconds: number;
  festpreisSeconds: number;
  aufNachweisSeconds: number;
  ohneBerechnungSeconds: number;
  gewaehrleistungSeconds: number;
}

export interface HeroUsageDayTotals {
  writtenSeconds: number;
  timesheetSeconds: number;
  festpreisSeconds: number;
}

// ── Datum-Helfer ─────────────────────────────────────────────────────

export function toIsoDateLocal(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function defaultDateRange(): { from: string; to: string } {
  return monthDateRange(currentMonthIso());
}

export function currentMonthIso(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
}

export function monthDateRange(monthIso: string): { from: string; to: string } {
  const [yearRaw, monthRaw] = monthIso.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: toIsoDateLocal(from), to: toIsoDateLocal(to) };
  }
  const from = new Date(year, month - 1, 1);
  const to = new Date(year, month, 0);
  return { from: toIsoDateLocal(from), to: toIsoDateLocal(to) };
}

export function shiftMonthIso(monthIso: string, delta: number): string {
  const [yearRaw, monthRaw] = monthIso.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const cursor = new Date(Number.isFinite(year) ? year : 2000, (Number.isFinite(month) ? month : 1) - 1 + delta, 1);
  return `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonthLabelDe(monthIso: string): string {
  const { from } = monthDateRange(monthIso);
  const parsed = new Date(`${from}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return monthIso;
  return parsed.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

export function currentYear(): number {
  return new Date().getFullYear();
}

export function yearDateRange(year: number): { from: string; to: string } {
  const normalized = Number.isFinite(year) ? Math.trunc(year) : currentYear();
  const from = new Date(normalized, 0, 1);
  const to = new Date(normalized, 11, 31);
  return { from: toIsoDateLocal(from), to: toIsoDateLocal(to) };
}

export function shiftYear(year: number, delta: number): number {
  const normalized = Number.isFinite(year) ? Math.trunc(year) : currentYear();
  return normalized + delta;
}

export function formatDateDe(isoDate: string | null | undefined): string {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("de-DE");
}

export function formatDurationHours(seconds: number | null | undefined): string {
  if (!Number.isFinite(seconds as number)) return "";
  return `${((seconds as number) / 3600).toFixed(2)} h`;
}

export function formatCalendarHours(seconds: number): string {
  const hours = Number.isFinite(seconds) ? seconds / 3600 : 0;
  return `${hours.toFixed(2).replace(".", ",")} h`;
}

export function formatExpectedHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return "—";
  return `${hours.toFixed(2).replace(".", ",")} h`;
}

export interface HeroExpectedHoursDay {
  date: string;
  expected_hours: number;
}

export interface HeroExpectedHoursByDayResponse {
  days: HeroExpectedHoursDay[];
  period?: { start: string; end: string };
}

// ── Fachlogik (portiert aus der klassischen Seite) ───────────────────

export function getHourMarkStatus(row: HeroUsageRow): HeroHourMarkStatus | null {
  const value = String(row.hour_mark_status ?? "").trim().toLowerCase();
  if (
    value === "festpreis" ||
    value === "auf_nachweis" ||
    value === "ohne_berechnung" ||
    value === "gewaehrleistung"
  ) {
    return value;
  }
  return null;
}

export function isFestpreisRow(row: HeroUsageRow): boolean {
  const mark = getHourMarkStatus(row);
  if (mark === "auf_nachweis" || mark === "ohne_berechnung" || mark === "gewaehrleistung") {
    return false;
  }
  if (mark === "festpreis" || row.fp_marked_local) return true;
  if (isFestpreisOnlyAuftragsart(row.auftragsart)) return true;
  if (isNwPlusFpAuftragsart(row.auftragsart)) return isFpComment(row.comment);
  return false;
}

function hasTimesheetAssignment(row: HeroUsageRow): boolean {
  return Boolean(row.timesheet_number || row.used_timesheet_id);
}

export function getAssignmentState(row: HeroUsageRow): HeroUsageAssignmentState {
  if (row.assignment_state) return row.assignment_state as HeroUsageAssignmentState;

  const mapped = hasTimesheetAssignment(row);
  const durationSeconds = Number(row.duration_in_seconds ?? row.duration ?? 0);
  const usedRaw = row.used_seconds;

  if (usedRaw === null || usedRaw === undefined) {
    return mapped ? "assigned" : "unassigned";
  }
  const used = Number(usedRaw);
  if (!Number.isFinite(used)) return mapped ? "assigned" : "unassigned";
  if (used === 0) return mapped ? "assigned" : "unassigned";
  if (used >= durationSeconds) return "assigned";
  return "partial";
}

const STATUS_LABELS: Record<HeroUsageAssignmentState, string> = {
  assigned: "Stundennachweis zugeordnet",
  unassigned: "kein Stundennachweis",
  partial: "teilweise Stundennachweis zugeordnet",
  unmapped: "kein Stundennachweis (kein Projektmapping)",
};

const HOUR_MARK_LABELS: Record<HeroHourMarkStatus, string> = {
  festpreis: "Festpreis",
  auf_nachweis: "Auf Nachweis",
  ohne_berechnung: "Ohne Berechnung",
  gewaehrleistung: "Gewährleistung",
};

export function getStatusInfo(row: HeroUsageRow): {
  isFestpreis: boolean;
  assignmentState: HeroUsageAssignmentState;
  classState:
    | "festpreis"
    | "auf_nachweis"
    | "ohne_berechnung"
    | "gewaehrleistung"
    | HeroUsageAssignmentState;
  status: string;
  hourMark: HeroHourMarkStatus | null;
} {
  const hourMark = getHourMarkStatus(row);
  const festpreis = isFestpreisRow(row);
  const assignmentState = getAssignmentState(row);
  if (hourMark && hourMark !== "festpreis") {
    let statusLabel = HOUR_MARK_LABELS[hourMark];
    if (hourMark === "auf_nachweis") {
      const number = row.hour_mark_timesheet_number;
      if (number != null && String(number).trim()) {
        statusLabel = `Auf Nachweis #${String(number).trim()}`;
      }
    }
    return {
      isFestpreis: false,
      assignmentState,
      classState: hourMark,
      status: statusLabel,
      hourMark,
    };
  }
  return {
    isFestpreis: festpreis,
    assignmentState,
    classState: festpreis ? "festpreis" : assignmentState,
    status: festpreis ? "Festpreis" : STATUS_LABELS[assignmentState] ?? "kein Stundennachweis",
    hourMark: festpreis ? "festpreis" : hourMark,
  };
}

function emptyTotals(): HeroUsageTotals {
  return {
    assignedSeconds: 0,
    unassignedSeconds: 0,
    unassignedMappedSeconds: 0,
    unmappedSeconds: 0,
    partialSeconds: 0,
    festpreisSeconds: 0,
    aufNachweisSeconds: 0,
    ohneBerechnungSeconds: 0,
    gewaehrleistungSeconds: 0,
  };
}

export function filterHeroUsageRows(
  rows: HeroUsageRow[],
  filters: Pick<HeroUsageFilters, "person" | "status" | "hideFullLicense">,
): { filtered: HeroUsageRow[]; totals: HeroUsageTotals } {
  const person = filters.person.trim().toLowerCase();
  const totals = emptyTotals();

  const filtered = rows.filter((row) => {
    if (filters.hideFullLicense) {
      const accountType = String(row.partner_account_type ?? "").trim().toLowerCase();
      if (accountType === "full") return false;
    }
    if (person) {
      const partnerName = String(row.partner_name ?? "").trim().toLowerCase();
      if (!partnerName.includes(person)) return false;
    }

    const mark = getHourMarkStatus(row);
    const festpreis = isFestpreisRow(row);
    const assignmentState = getAssignmentState(row);
    if (filters.status === "festpreis" && !festpreis) return false;
    if (filters.status === "auf_nachweis" && mark !== "auf_nachweis") return false;
    if (filters.status === "ohne_berechnung" && mark !== "ohne_berechnung") return false;
    if (filters.status === "gewaehrleistung" && mark !== "gewaehrleistung") return false;
    if (
      filters.status === "assigned" &&
      (festpreis || mark != null || assignmentState !== "assigned")
    ) {
      return false;
    }
    if (
      filters.status === "unassigned" &&
      (festpreis || mark != null || assignmentState !== "unassigned")
    ) {
      return false;
    }
    if (
      filters.status === "unmapped" &&
      (festpreis || mark != null || assignmentState !== "unmapped")
    ) {
      return false;
    }

    const durationSeconds = Number(row.duration_in_seconds ?? row.duration ?? 0);
    if (mark === "auf_nachweis") {
      totals.aufNachweisSeconds += durationSeconds;
    } else if (mark === "ohne_berechnung") {
      totals.ohneBerechnungSeconds += durationSeconds;
    } else if (mark === "gewaehrleistung") {
      totals.gewaehrleistungSeconds += durationSeconds;
    } else if (festpreis) {
      totals.festpreisSeconds += durationSeconds;
    } else if (assignmentState === "assigned") {
      totals.assignedSeconds += durationSeconds;
    } else if (assignmentState === "unassigned") {
      totals.unassignedMappedSeconds += durationSeconds;
    } else if (assignmentState === "unmapped") {
      totals.unmappedSeconds += durationSeconds;
    } else if (assignmentState === "partial") {
      totals.partialSeconds += durationSeconds;
    }

    return true;
  });

  totals.unassignedSeconds = totals.unassignedMappedSeconds + totals.unmappedSeconds;
  return { filtered, totals };
}

export function buildDailyTotals(rows: HeroUsageRow[]): Map<string, HeroUsageDayTotals> {
  const totals = new Map<string, HeroUsageDayTotals>();

  for (const row of rows) {
    // Immer YYYY-MM-DD – falls die API Datetime-Strings liefert.
    const workDate = String(row.work_date ?? "").trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate)) continue;

    const durationSeconds = Number(row.duration_in_seconds ?? row.duration ?? 0);
    const normalized = Number.isFinite(durationSeconds) ? durationSeconds : 0;

    let day = totals.get(workDate);
    if (!day) {
      day = { writtenSeconds: 0, timesheetSeconds: 0, festpreisSeconds: 0 };
      totals.set(workDate, day);
    }
    day.writtenSeconds += normalized;

    if (isFestpreisRow(row)) {
      day.festpreisSeconds += normalized;
    }

    const usedRaw = row.used_seconds;
    if (usedRaw === null || usedRaw === undefined) continue;
    const used = Number(usedRaw);
    if (!Number.isFinite(used)) continue;
    day.timesheetSeconds += Math.max(0, Math.min(used, normalized));
  }

  return totals;
}

export interface HeroUsageRowBreakdown {
  unmappedSeconds: number;
  unassignedSeconds: number;
  withProofSeconds: number;
  festpreisSeconds: number;
  aufNachweisSeconds: number;
  ohneBerechnungSeconds: number;
  gewaehrleistungSeconds: number;
}

function emptyBreakdown(): HeroUsageRowBreakdown {
  return {
    unmappedSeconds: 0,
    unassignedSeconds: 0,
    withProofSeconds: 0,
    festpreisSeconds: 0,
    aufNachweisSeconds: 0,
    ohneBerechnungSeconds: 0,
    gewaehrleistungSeconds: 0,
  };
}

export function rowSecondsBreakdown(row: HeroUsageRow): HeroUsageRowBreakdown {
  const duration = Number(row.duration_in_seconds ?? row.duration ?? 0);
  if (!Number.isFinite(duration) || duration <= 0) {
    return emptyBreakdown();
  }

  const mark = getHourMarkStatus(row);
  if (mark === "auf_nachweis") {
    return { ...emptyBreakdown(), aufNachweisSeconds: duration };
  }
  if (mark === "ohne_berechnung") {
    return { ...emptyBreakdown(), ohneBerechnungSeconds: duration };
  }
  if (mark === "gewaehrleistung") {
    return { ...emptyBreakdown(), gewaehrleistungSeconds: duration };
  }

  if (isFestpreisRow(row)) {
    return { ...emptyBreakdown(), festpreisSeconds: duration };
  }

  const state = getAssignmentState(row);
  if (state === "unmapped") {
    return { ...emptyBreakdown(), unmappedSeconds: duration };
  }
  if (state === "unassigned") {
    return { ...emptyBreakdown(), unassignedSeconds: duration };
  }
  if (state === "assigned") {
    return { ...emptyBreakdown(), withProofSeconds: duration };
  }

  const used = Number(row.used_seconds ?? 0);
  const proof = Number.isFinite(used) ? Math.max(0, Math.min(used, duration)) : 0;
  return {
    ...emptyBreakdown(),
    unassignedSeconds: duration - proof,
    withProofSeconds: proof,
  };
}

export interface ProjectOpenHoursEntry {
  auftragId: number | null;
  projekt: string;
  openSeconds: number;
}

export function buildProjectOpenHoursRanking(rows: HeroUsageRow[]): ProjectOpenHoursEntry[] {
  const byProject = new Map<string, ProjectOpenHoursEntry>();

  for (const row of rows) {
    if (isFestpreisRow(row)) continue;

    const breakdown = rowSecondsBreakdown(row);
    const openSeconds = breakdown.unassignedSeconds;
    if (openSeconds <= 0) continue;

    const auftragId = row.auftrag_id;
    const projekt = String(row.projekt ?? "").trim() || "Unbekannt";
    const key = auftragId != null ? `id:${auftragId}` : `name:${projekt.toLowerCase()}`;

    const existing = byProject.get(key);
    if (existing) {
      existing.openSeconds += openSeconds;
      continue;
    }
    byProject.set(key, { auftragId, projekt, openSeconds });
  }

  return [...byProject.values()].sort((a, b) => b.openSeconds - a.openSeconds);
}

export interface EmployeeHoursEntry {
  partnerName: string;
  unmappedSeconds: number;
  unassignedSeconds: number;
  withProofSeconds: number;
  festpreisSeconds: number;
  aufNachweisSeconds: number;
  ohneBerechnungSeconds: number;
  gewaehrleistungSeconds: number;
  openSeconds: number;
  totalSeconds: number;
}

export function buildEmployeeHoursRanking(rows: HeroUsageRow[]): EmployeeHoursEntry[] {
  const byPartner = new Map<string, EmployeeHoursEntry>();

  for (const row of rows) {
    const duration = Number(row.duration_in_seconds ?? row.duration ?? 0);
    if (!Number.isFinite(duration) || duration <= 0) continue;

    const partnerName = String(row.partner_name ?? "").trim() || "Unbekannt";
    const breakdown = rowSecondsBreakdown(row);

    const existing = byPartner.get(partnerName);
    if (existing) {
      existing.unmappedSeconds += breakdown.unmappedSeconds;
      existing.unassignedSeconds += breakdown.unassignedSeconds;
      existing.withProofSeconds += breakdown.withProofSeconds;
      existing.festpreisSeconds += breakdown.festpreisSeconds;
      existing.aufNachweisSeconds += breakdown.aufNachweisSeconds;
      existing.ohneBerechnungSeconds += breakdown.ohneBerechnungSeconds;
      existing.gewaehrleistungSeconds += breakdown.gewaehrleistungSeconds;
      existing.openSeconds += breakdown.unmappedSeconds + breakdown.unassignedSeconds;
      existing.totalSeconds += duration;
      continue;
    }

    byPartner.set(partnerName, {
      partnerName,
      unmappedSeconds: breakdown.unmappedSeconds,
      unassignedSeconds: breakdown.unassignedSeconds,
      withProofSeconds: breakdown.withProofSeconds,
      festpreisSeconds: breakdown.festpreisSeconds,
      aufNachweisSeconds: breakdown.aufNachweisSeconds,
      ohneBerechnungSeconds: breakdown.ohneBerechnungSeconds,
      gewaehrleistungSeconds: breakdown.gewaehrleistungSeconds,
      openSeconds: breakdown.unmappedSeconds + breakdown.unassignedSeconds,
      totalSeconds: duration,
    });
  }

  return [...byPartner.values()].sort((a, b) => b.openSeconds - a.openSeconds);
}

export function normalizedPartnerName(row: HeroUsageRow): string {
  return String(row.partner_name ?? "").trim() || "Unbekannt";
}

export function projectLabelFromRow(row: HeroUsageRow): string {
  const auftragId = row.auftrag_id;
  if (auftragId == null) return "Ohne Projekt";
  return String(row.projekt ?? "").trim() || "Unbekannt";
}

export function projectKeyFromRow(row: HeroUsageRow): string {
  const auftragId = row.auftrag_id;
  if (auftragId == null) return "unmapped";
  return `id:${auftragId}`;
}

export function matchesProjectOpenHoursEntry(
  row: HeroUsageRow,
  project: ProjectOpenHoursEntry,
): boolean {
  if (project.auftragId != null) {
    return row.auftrag_id === project.auftragId;
  }
  return projectLabelFromRow(row).toLowerCase() === project.projekt.toLowerCase();
}

export interface OpenHoursDetailSection {
  label: string;
  entries: HeroUsageRow[];
  totalSeconds: number;
  unmappedSeconds: number;
  unassignedSeconds: number;
  withProofSeconds: number;
  festpreisSeconds: number;
  aufNachweisSeconds: number;
  ohneBerechnungSeconds: number;
  gewaehrleistungSeconds: number;
}

export interface ProjectOpenHoursDetail {
  project: ProjectOpenHoursEntry;
  sections: OpenHoursDetailSection[];
  totalSeconds: number;
}

export interface EmployeeHoursDetail {
  partnerName: string;
  sections: OpenHoursDetailSection[];
  totals: EmployeeHoursEntry;
}

function rowDurationSeconds(row: HeroUsageRow): number {
  const duration = Number(row.duration_in_seconds ?? row.duration ?? 0);
  return Number.isFinite(duration) && duration > 0 ? duration : 0;
}

function sortEntriesByDate(entries: HeroUsageRow[]): HeroUsageRow[] {
  return [...entries].sort((a, b) => {
    const dateCmp = String(a.work_date ?? "").localeCompare(String(b.work_date ?? ""));
    if (dateCmp !== 0) return dateCmp;
    return normalizedPartnerName(a).localeCompare(normalizedPartnerName(b), "de");
  });
}

function aggregateSection(entries: HeroUsageRow[]): Omit<OpenHoursDetailSection, "label" | "entries"> {
  let totalSeconds = 0;
  let unmappedSeconds = 0;
  let unassignedSeconds = 0;
  let withProofSeconds = 0;
  let festpreisSeconds = 0;
  let aufNachweisSeconds = 0;
  let ohneBerechnungSeconds = 0;
  let gewaehrleistungSeconds = 0;

  for (const row of entries) {
    const duration = rowDurationSeconds(row);
    if (duration <= 0) continue;
    const breakdown = rowSecondsBreakdown(row);
    totalSeconds += duration;
    unmappedSeconds += breakdown.unmappedSeconds;
    unassignedSeconds += breakdown.unassignedSeconds;
    withProofSeconds += breakdown.withProofSeconds;
    festpreisSeconds += breakdown.festpreisSeconds;
    aufNachweisSeconds += breakdown.aufNachweisSeconds;
    ohneBerechnungSeconds += breakdown.ohneBerechnungSeconds;
    gewaehrleistungSeconds += breakdown.gewaehrleistungSeconds;
  }

  return {
    totalSeconds,
    unmappedSeconds,
    unassignedSeconds,
    withProofSeconds,
    festpreisSeconds,
    aufNachweisSeconds,
    ohneBerechnungSeconds,
    gewaehrleistungSeconds,
  };
}

export function buildProjectOpenHoursDetail(
  rows: HeroUsageRow[],
  project: ProjectOpenHoursEntry,
): ProjectOpenHoursDetail {
  const byEmployee = new Map<string, HeroUsageRow[]>();

  for (const row of rows) {
    if (!matchesProjectOpenHoursEntry(row, project)) continue;
    if (rowSecondsBreakdown(row).unassignedSeconds <= 0) continue;

    const partnerName = normalizedPartnerName(row);
    const list = byEmployee.get(partnerName) ?? [];
    list.push(row);
    byEmployee.set(partnerName, list);
  }

  const sections = [...byEmployee.entries()]
    .map(([label, entries]) => {
      const sortedEntries = sortEntriesByDate(entries);
      const totalSeconds = sortedEntries.reduce(
        (sum, entry) => sum + rowSecondsBreakdown(entry).unassignedSeconds,
        0,
      );
      return {
        label,
        entries: sortedEntries,
        totalSeconds,
        unmappedSeconds: 0,
        unassignedSeconds: totalSeconds,
        withProofSeconds: 0,
        festpreisSeconds: 0,
        aufNachweisSeconds: 0,
        ohneBerechnungSeconds: 0,
        gewaehrleistungSeconds: 0,
      };
    })
    .sort((a, b) => b.totalSeconds - a.totalSeconds || a.label.localeCompare(b.label, "de"));

  return {
    project,
    sections,
    totalSeconds: sections.reduce((sum, section) => sum + section.totalSeconds, 0),
  };
}

export function buildEmployeeHoursDetail(
  rows: HeroUsageRow[],
  partnerName: string,
): EmployeeHoursDetail {
  const byProject = new Map<string, { label: string; entries: HeroUsageRow[] }>();

  for (const row of rows) {
    if (normalizedPartnerName(row) !== partnerName) continue;
    if (rowDurationSeconds(row) <= 0) continue;

    const key = projectKeyFromRow(row);
    const label = projectLabelFromRow(row);
    const bucket = byProject.get(key) ?? { label, entries: [] };
    bucket.entries.push(row);
    byProject.set(key, bucket);
  }

  const sections = [...byProject.values()]
    .map(({ label, entries }) => {
      const sortedEntries = sortEntriesByDate(entries);
      return {
        label,
        entries: sortedEntries,
        ...aggregateSection(sortedEntries),
      };
    })
    .sort(
      (a, b) =>
        b.unassignedSeconds - a.unassignedSeconds ||
        b.unmappedSeconds - a.unmappedSeconds ||
        a.label.localeCompare(b.label, "de"),
    );

  const totals = sections.reduce<EmployeeHoursEntry>(
    (acc, section) => {
      acc.unmappedSeconds += section.unmappedSeconds;
      acc.unassignedSeconds += section.unassignedSeconds;
      acc.withProofSeconds += section.withProofSeconds;
      acc.festpreisSeconds += section.festpreisSeconds;
      acc.aufNachweisSeconds += section.aufNachweisSeconds;
      acc.ohneBerechnungSeconds += section.ohneBerechnungSeconds;
      acc.gewaehrleistungSeconds += section.gewaehrleistungSeconds;
      acc.openSeconds += section.unmappedSeconds + section.unassignedSeconds;
      acc.totalSeconds += section.totalSeconds;
      return acc;
    },
    {
      partnerName,
      unmappedSeconds: 0,
      unassignedSeconds: 0,
      withProofSeconds: 0,
      festpreisSeconds: 0,
      aufNachweisSeconds: 0,
      ohneBerechnungSeconds: 0,
      gewaehrleistungSeconds: 0,
      openSeconds: 0,
      totalSeconds: 0,
    },
  );

  return { partnerName, sections, totals };
}

export interface HeroMarkFestpreisItemResult {
  hero_uuid: string;
  comment?: string | null;
  reason?: string | null;
  error?: string | null;
  local_only?: boolean;
  status?: string | null;
}

export interface HeroMarkFestpreisResult {
  updated: HeroMarkFestpreisItemResult[];
  skipped: HeroMarkFestpreisItemResult[];
  failed: HeroMarkFestpreisItemResult[];
}

export async function markHeroEntriesFestpreis(
  entries: Array<{ hero_uuid: string; comment?: string | null }>,
): Promise<HeroMarkFestpreisResult> {
  return markHeroEntriesStatus("festpreis", entries);
}

export async function markHeroEntriesStatus(
  status: HeroHourMarkStatus,
  entries: Array<{ hero_uuid: string; comment?: string | null }>,
  timesheetId?: number | null,
): Promise<HeroMarkFestpreisResult> {
  return api.post<HeroMarkFestpreisResult>("/reports/hero-usage/mark-status", {
    status,
    entries,
    ...(status === "auf_nachweis" && timesheetId != null
      ? { timesheet_id: timesheetId }
      : {}),
  });
}

export interface TimesheetByAuftragOption {
  id: number;
  timesheet_number: number | string | null;
  status: string;
  date_from: string | null;
  date_to: string | null;
  auftrag_id: number | null;
}

export async function fetchTimesheetsByAuftrag(
  auftragId: number,
): Promise<TimesheetByAuftragOption[]> {
  return api.get<TimesheetByAuftragOption[]>(`/timesheets/by-auftrag/${auftragId}`);
}

// ── Laden + Modul-Cache (stale-while-revalidate) ─────────────────────

interface ReportCacheEntry {
  fetchedAt: number;
  rows: HeroUsageRow[];
}

const reportCache = new Map<string, ReportCacheEntry>();
const FRESH_MS = 60_000;

function cacheKey(from: string, to: string): string {
  return `${from}:${to}`;
}

export function getCachedHeroUsageReport(
  from: string,
  to: string,
): { rows: HeroUsageRow[]; isFresh: boolean } | null {
  const entry = reportCache.get(cacheKey(from, to));
  if (!entry) return null;
  return { rows: entry.rows, isFresh: Date.now() - entry.fetchedAt < FRESH_MS };
}

export function invalidateHeroUsageReportCache(): void {
  reportCache.clear();
}

export async function fetchHeroUsageReport(from: string, to: string): Promise<HeroUsageRow[]> {
  const params = new URLSearchParams({
    start: from,
    end: to,
    include_unmapped: "true",
    hide_full_license: "false",
    status: "all",
  });
  const rows = await api.get<HeroUsageRow[]>(`/reports/hero-usage?${params}`);
  const normalized = Array.isArray(rows) ? rows : [];
  reportCache.set(cacheKey(from, to), { fetchedAt: Date.now(), rows: normalized });
  return normalized;
}

export async function fetchHeroExpectedHoursByDay(
  from: string,
  to: string,
): Promise<Map<string, number>> {
  const params = new URLSearchParams({ start: from, end: to });
  const data = await api.get<HeroExpectedHoursByDayResponse>(
    `/reports/hero-usage/expected-hours-by-day?${params}`,
  );
  const map = new Map<string, number>();
  for (const day of data?.days ?? []) {
    const iso = String(day.date ?? "").trim().slice(0, 10);
    if (!iso) continue;
    const hours = Number(day.expected_hours);
    map.set(iso, Number.isFinite(hours) ? hours : 0);
  }
  return map;
}
